import React, { useEffect } from 'react';
import { BrowserRouter, Routes, Route, useLocation } from 'react-router-dom';
import { AuthProvider } from './context/AuthContext';
import { LanguageProvider } from './context/LanguageContext';
import Homepage from './pages/Homepage';
import Login from './pages/Login';
import AuthCallback from './pages/AuthCallback';
import Dashboard from './pages/Dashboard';
import PostAd from './pages/PostAd';
import Subscriptions from './pages/Subscriptions';
import ListingDetail from './pages/ListingDetail';
import Wishlist from './pages/Wishlist';
import ChatPage from './pages/ChatPage';
import AdminPanel from './pages/AdminPanel';
import EditListing from './pages/EditListing';
import TermsPage from './pages/TermsPage';
import PrivacyPage from './pages/PrivacyPage';
import ContactPage from './pages/ContactPage';
import SearchResults from './pages/SearchResults';
import CategoryBrowse from './pages/CategoryBrowse';
import EditProfile from './pages/EditProfile';
import CompleteProfile from './pages/CompleteProfile';
import SellerPage from './pages/SellerPage';
import FloatingActionStack from './components/FloatingActionStack';
import SiteFooter from './components/SiteFooter';
import OneSignalProvider from './components/OneSignalProvider';
import WebToNativeBridge from './components/WebToNativeBridge';
import './App.css';

function AppRouter() {
  const location = useLocation();

  useEffect(() => {
    document.title = 'GenzoX - Real People, Real Deals';
  }, []);

  useEffect(() => {
    window.scrollTo(0, 0);
    // Meta Pixel — fire a PageView on every SPA route change so client-side
    // navigations are tracked (fbq bootstrap in index.html only fires on
    // the initial HTML load).
    if (typeof window !== 'undefined' && typeof window.fbq === 'function') {
      try { window.fbq('track', 'PageView'); } catch (_) { /* ignore */ }
    }
  }, [location.pathname]);

  useEffect(() => {
    const script = document.createElement('script');
    script.src = 'https://checkout.razorpay.com/v1/checkout.js';
    script.async = true;
    document.body.appendChild(script);
    return () => { document.body.removeChild(script); };
  }, []);

  // Iter49: register service worker so notifications work on iOS Safari PWA installs.
  // On iOS 16.4+, `new Notification()` is NOT supported — only ServiceWorkerRegistration.showNotification()
  // dispatched from a registered SW. Registering early ensures ChatPage can use SW notifications immediately.
  useEffect(() => {
    if ('serviceWorker' in navigator) {
      navigator.serviceWorker.register('/sw.js').catch((err) => {
        console.warn('Service worker registration failed:', err);
      });
    }
  }, []);

  if (location.hash?.includes('session_id=')) {
    return <AuthCallback />;
  }

  return (
    <Routes>
      <Route path="/" element={<Homepage />} />
      <Route path="/login" element={<Login />} />
      <Route path="/auth/callback" element={<AuthCallback />} />
      <Route path="/dashboard" element={<Dashboard />} />
      <Route path="/post-ad" element={<PostAd />} />
      <Route path="/subscriptions" element={<Subscriptions />} />
      <Route path="/listing/:id" element={<ListingDetail />} />
      <Route path="/wishlist" element={<Wishlist />} />
      <Route path="/chat" element={<ChatPage />} />
      <Route path="/chat/:conversationId" element={<ChatPage />} />
      <Route path="/admin" element={<AdminPanel />} />
      <Route path="/edit-listing/:id" element={<EditListing />} />
      <Route path="/terms" element={<TermsPage />} />
      <Route path="/privacy" element={<PrivacyPage />} />
      <Route path="/contact" element={<ContactPage />} />
      <Route path="/contact-us" element={<ContactPage />} />
      <Route path="/search" element={<SearchResults />} />
      <Route path="/category/:categoryId" element={<CategoryBrowse />} />
      <Route path="/profile" element={<EditProfile />} />
      <Route path="/complete-profile" element={<CompleteProfile />} />
      <Route path="/seller/:userId" element={<SellerPage />} />
    </Routes>
  );
}

function App() {
  return (
    <AuthProvider>
      <LanguageProvider>
        <OneSignalProvider>
          <WebToNativeBridge>
            <BrowserRouter>
              <AppRouter />
              {/* iter130 — Footer + FAB stack render on every route. */}
              <SiteFooter />
              <FloatingActionStack />
            </BrowserRouter>
          </WebToNativeBridge>
        </OneSignalProvider>
      </LanguageProvider>
    </AuthProvider>
  );
}

export default App;
